// IND-041 / IND-043: ventanas de la Hoja de recuento: elegir hoja del libro,
// añadir fila, materiales que entraron y comparación con el libro.
import { useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { analyzeRecountWorkbook } from '../services/industrialCostService';
import type {
  RecountCompareResult,
  RecountMissingMaterial,
  RecountRowDraft,
  RecountSheetRow,
} from '../services/industrialCostService';
import { bookErrorText, COMPARE_FIELDS, COMPARE_KINDS, compareCsv, fmtQty, isSupplierCode } from '../utils/recountSheet';
import { parseKg } from '../utils/wasteControl';

const MUTED = '#94a3b8';
const BUTTON = { textTransform: 'none', fontWeight: 800 };

export function BookSheetDialog({
  title,
  actionLabel,
  warning,
  suggestedSheet,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  title: string;
  actionLabel: string;
  warning?: string;
  suggestedSheet: string;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (file: File, sheetName: string) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [sheets, setSheets] = useState<string[]>([]);
  const [sheetName, setSheetName] = useState('');
  const [reading, setReading] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);

  const choose = async (next: File) => {
    setFile(next);
    setSheets([]);
    setReadError(null);
    setReading(true);
    try {
      const analysis = await analyzeRecountWorkbook(next);
      const names = analysis.sheets.map((sheet) => sheet.sheet_name);
      setSheets(names);
      setSheetName(names.includes(suggestedSheet) ? suggestedSheet : names[0] ?? '');
    } catch (exc) {
      setReadError(bookErrorText(exc));
    } finally {
      setReading(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 900 }}>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          {warning ? <Alert severity="warning">{warning}</Alert> : null}
          <input
            ref={input}
            type="file"
            accept=".xlsx"
            hidden
            onChange={(event) => {
              const next = event.target.files?.[0];
              event.target.value = '';
              if (next) void choose(next);
            }}
          />
          <Button variant="outlined" onClick={() => input.current?.click()} sx={BUTTON}>
            {file ? file.name : 'Elegir el libro de recuento'}
          </Button>
          {reading ? <Typography sx={{ color: MUTED, fontSize: 13 }}>Leyendo el libro…</Typography> : null}
          {readError ? <Alert severity="error">{readError}</Alert> : null}
          {sheets.length ? (
            <TextField select size="small" label="Hoja" value={sheetName} onChange={(event) => setSheetName(event.target.value)}>
              {sheets.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}
            </TextField>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={!file || !sheetName || busy}
          onClick={() => file && onSubmit(file, sheetName)}
          sx={BUTTON}
        >
          {actionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type RowForm = {
  material_code: string;
  reference: string;
  supplier: string;
  supplier_code: string;
  class_label: string;
  destination: string;
  unit: 'kg' | 'unidades';
  share: string;
  price: string;
  after_row_id: string;
};

export function AddRowDialog({
  rows,
  initial,
  title,
  requireDetails,
  busy,
  failed,
  onClose,
  onSave,
}: {
  rows: RecountSheetRow[];
  initial?: Partial<RowForm>;
  title: string;
  /** IND-043: DESTINO, CLASE y % se completan a mano antes de guardar. */
  requireDetails?: boolean;
  busy: boolean;
  failed: boolean;
  onClose: () => void;
  onSave: (draft: RecountRowDraft) => void;
}) {
  const [form, setForm] = useState<RowForm>({
    material_code: '', reference: '', supplier: '', supplier_code: '', class_label: '', destination: '',
    unit: 'kg', share: '', price: '', after_row_id: '', ...initial,
  });
  const set = (key: keyof RowForm) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));
  const share = parseKg(form.share);
  const price = parseKg(form.price);
  const invalid =
    !form.material_code.trim() ||
    (form.supplier_code.trim() !== '' && !isSupplierCode(form.supplier_code.trim())) ||
    (share !== null && !(share >= 0 && share <= 1)) ||
    (price !== null && !(price >= 0)) ||
    (requireDetails && (!form.destination.trim() || !form.class_label.trim() || share === null));

  const field = (key: keyof RowForm, label: string) => (
    <TextField key={key} size="small" label={label} value={form[key]} onChange={set(key)} />
  );
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 900 }}>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.25}>
          {field('material_code', 'CÓDIGO')}
          {field('reference', 'REFERENCIA')}
          {field('supplier', 'PROVEEDOR')}
          {field('supplier_code', 'CÓDIGO PROVEEDOR')}
          {field('class_label', 'CLASE')}
          {field('destination', 'DESTINO')}
          <TextField select size="small" label="Unidad" value={form.unit} onChange={set('unit')}>
            <MenuItem value="kg">kg</MenuItem>
            <MenuItem value="unidades">unidades</MenuItem>
          </TextField>
          {field('share', '% DESTINO 1 (de 0 a 1)')}
          {field('price', 'PRECIO')}
          <TextField select size="small" label="Colocar" value={form.after_row_id} onChange={set('after_row_id')}>
            <MenuItem value="">Al final</MenuItem>
            {rows.map((row, index) => (
              <MenuItem key={row.id} value={row.id}>
                {`Debajo de la fila ${index + 1}: ${row.material_code}${row.supplier ? ` · ${row.supplier}` : ''}`}
              </MenuItem>
            ))}
          </TextField>
          <Typography sx={{ color: MUTED, fontSize: 12 }}>En esta fase no da de alta el material en PROA.</Typography>
          {failed ? <Alert severity="error">No se pudo guardar. Inténtalo de nuevo.</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={Boolean(invalid) || busy}
          onClick={() => onSave({
            material_code: form.material_code,
            reference: form.reference || null,
            supplier: form.supplier || null,
            supplier_code: form.supplier_code || null,
            class_label: form.class_label || null,
            destination: form.destination || null,
            unit: form.unit,
            share,
            price,
            after_row_id: form.after_row_id || null,
          })}
          sx={BUTTON}
        >
          Guardar fila
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function MissingMaterialsDialog({
  monthName,
  items,
  busy,
  onClose,
  onAdd,
  onDismiss,
}: {
  monthName: string;
  items: RecountMissingMaterial[];
  busy: boolean;
  onClose: () => void;
  onAdd: (item: RecountMissingMaterial) => void;
  onDismiss: (item: RecountMissingMaterial) => void;
}) {
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 900 }}>{`Materiales que entraron en ${monthName} y no están en la hoja`}</DialogTitle>
      <DialogContent dividers>
        {items.length === 0 ? (
          <Typography sx={{ color: MUTED, fontSize: 13 }}>No queda ninguno por revisar.</Typography>
        ) : (
          <Stack spacing={1}>
            {items.map((item) => (
              <Stack
                key={`${item.key_code}|${item.delivery_key}`}
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                sx={{ p: 1, borderRadius: 2, border: '1px solid rgba(148,163,184,0.2)' }}
              >
                <Stack sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
                    {[item.material_code, item.reference].filter(Boolean).join(' · ')}
                  </Typography>
                  <Typography sx={{ color: MUTED, fontSize: 12.5 }}>
                    {`${item.delivery_name} · ${fmtQty(item.quantity)} en albaranes`}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                  <Button size="small" disabled={busy} onClick={() => onDismiss(item)} sx={{ textTransform: 'none' }}>
                    Descartar
                  </Button>
                  <Button size="small" variant="outlined" disabled={busy} onClick={() => onAdd(item)} sx={{ textTransform: 'none' }}>
                    Añadir
                  </Button>
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" sx={BUTTON}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

function download(text: string, name: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}

export function CompareResultDialog({
  sheetName,
  result,
  onClose,
}: {
  sheetName: string;
  result: RecountCompareResult;
  onClose: () => void;
}) {
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 900 }}>{`Comparación con el libro · ${sheetName}`}</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ fontWeight: 800, mb: 1 }}>
          {`${result.equal} filas iguales, ${result.different} con diferencias · ${result.only_proa} solo en PROA · ${result.only_book} solo en el libro`}
        </Typography>
        {result.rows.length ? (
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { fontSize: 12.5 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Caso</TableCell>
                  <TableCell>Código</TableCell>
                  <TableCell>Proveedor</TableCell>
                  <TableCell>Diferencias (PROA → libro)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.rows.map((row, index) => (
                  <TableRow key={`${row.material_code}|${row.supplier}|${index}`}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{COMPARE_KINDS[row.kind]}</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>{row.material_code}</TableCell>
                    <TableCell>{row.supplier ?? '—'}</TableCell>
                    <TableCell>
                      {row.differences
                        .map((item) => `${COMPARE_FIELDS[item.field]}: ${fmtQty(item.proa)} → ${fmtQty(item.book)}`)
                        .join(' · ') || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => download(compareCsv(result), `comparacion-${sheetName}.csv`)} sx={{ textTransform: 'none' }}>
          Descargar
        </Button>
        <Button onClick={onClose} variant="contained" sx={BUTTON}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
